<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "./Modal.vue";
import SearchField from "./SearchField.vue";
import Avatar from "./Avatar.vue";
import Button from "./Button.vue";
import { t } from "../i18n";
import { profileTone, type ProfileTone } from "../composables/profileColors";

/**
 * Where forwarded messages go, as WhatsApp Web's "Encaminhar mensagem para": conversations that
 * can take a message now (open, inside the 24-hour window) and colleagues, who get it in a note.
 */
export interface ForwardConversation {
  id: string;
  name: string;
  /** The phone or what else tells two people with the same name apart. */
  detail?: string | null;
  tone?: ProfileTone;
}
export interface ForwardColleague {
  id: string;
  name: string;
}
const props = defineProps<{
  open: boolean;
  /** How many messages go. */
  count: number;
  conversations: ForwardConversation[];
  colleagues: ForwardColleague[];
  loading?: boolean;
  busy?: boolean;
}>();
const emit = defineEmits<{
  close: [];
  /** The search typed, for the host to look for conversations. */
  search: [query: string];
  send: [
    payload: {
      conversationIds: string[];
      memberIds: string[];
      comment: string;
    },
  ];
}>();
/** WhatsApp forwards to at most five chats at a time. */
const MAX = 5;
const query = ref("");
const chats = ref<string[]>([]);
const people = ref<string[]>([]);
const comment = ref("");
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    query.value = "";
    chats.value = [];
    people.value = [];
    comment.value = "";
  },
);
watch(query, (q) => emit("search", q.trim()));
const total = computed(() => chats.value.length + people.value.length);
const shownColleagues = computed(() => {
  const q = query.value.trim().toLowerCase();
  return props.colleagues.filter((c) => !q || c.name.toLowerCase().includes(q));
});
function toggle(list: string[], id: string) {
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
  else if (total.value < MAX) list.push(id);
}
</script>
<template>
  <Modal
    :open="open"
    :title="t('forward.title', { count })"
    @close="emit('close')"
  >
    <div class="forward-picker">
      <SearchField v-model="query" :placeholder="t('forward.search')" />
      <h4 class="template-group">{{ t("forward.chats") }}</h4>
      <p v-if="loading" class="muted small">{{ t("inbox.loading") }}</p>
      <p v-else-if="!conversations.length" class="muted small">
        {{ t("forward.no_chats") }}
      </p>
      <label
        v-for="c in conversations"
        :key="c.id"
        class="list-row forward-row"
      >
        <Avatar :name="c.name" :tone="c.tone" :size="36" />
        <span class="grow"
          ><strong>{{ c.name }}</strong
          ><small v-if="c.detail" class="muted">{{ c.detail }}</small></span
        >
        <input
          type="checkbox"
          class="wa-check"
          :checked="chats.includes(c.id)"
          :disabled="!chats.includes(c.id) && total >= MAX"
          @change="toggle(chats, c.id)"
        />
      </label>
      <small class="muted">{{ t("forward.chats_hint") }}</small>
      <template v-if="shownColleagues.length">
        <h4 class="template-group">{{ t("forward.team") }}</h4>
        <label
          v-for="c in shownColleagues"
          :key="c.id"
          class="list-row forward-row"
        >
          <Avatar :name="c.name" :tone="profileTone(null, c.id)" :size="36" />
          <span class="grow"
            ><strong>{{ c.name }}</strong
            ><small class="muted">{{ t("forward.team_hint") }}</small></span
          >
          <input
            type="checkbox"
            class="wa-check"
            :checked="people.includes(c.id)"
            :disabled="!people.includes(c.id) && total >= MAX"
            @change="toggle(people, c.id)"
          />
        </label>
      </template>
      <label v-if="people.length" class="wa-field"
        ><span>{{ t("forward.comment") }}</span
        ><textarea
          placeholder=" "
          v-model="comment"
          rows="2"
          maxlength="1000"
        />
      </label>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        icon="forward"
        :loading="busy"
        :disabled="!total"
        @click="
          emit('send', {
            conversationIds: [...chats],
            memberIds: [...people],
            comment: comment.trim(),
          })
        "
        >{{ t("forward.send") }}</Button
      >
    </template>
  </Modal>
</template>
